import { useAlgoliaConfigStore } from '@algolia-search/stores/config';
import type { LocaleConfig } from '@algolia-search/types';
import { useContext, useRoute } from '@nuxtjs/composition-api';
import type { SearchClient } from 'algoliasearch';
import algoliasearch from 'algoliasearch';

const clientCache = new WeakMap<object, SearchClient>();

export const useAlgoliaSearchClient = (
  isHeaderContext = false
): {
  searchClient: SearchClient;
  clientConfig: LocaleConfig;
} => {
  const {
    app: { i18n },
    $algoliaSearch
  } = useContext();
  const route = useRoute();
  const localeCode = i18n.localeProperties.code;
  const clientConfig = $algoliaSearch.localeConfigs[localeCode] ?? {
    appId: $algoliaSearch.defaultAppId,
    apiKey: $algoliaSearch.defaultApiKey,
    analyticsApiKey: $algoliaSearch.defaultAnalyticsApiKey
  };
  const cacheKeyObject = { localeCode };

  // Cache client by cacheKeyObject (to avoid creating client every time composable is triggered)
  if (!clientCache.has(cacheKeyObject)) {
    clientCache.set(cacheKeyObject, algoliasearch(clientConfig.appId, clientConfig.apiKey));
  }
  const client = clientCache.get(cacheKeyObject);
  const { autocompleteMinimumChars } = useAlgoliaConfigStore();

  const lastRequestCache: {
    key: string;
    promise: ReturnType<SearchClient['search']> | null;
  } = {
    key: '',
    promise: null
  };

  /**
   * Normalize request to a simplified cache key based only on indexName + query.
   */
  const normalizeRequest = (requests) =>
    JSON.stringify(
      requests.map(({ indexName, params }) => ({
        indexName,
        query: params?.query || ''
      }))
    );

  const createEmptyResults = (requests) => ({
    results: requests.map(() => ({
      hits: [],
      nbHits: 0,
      nbPages: 0,
      page: 0,
      processingTimeMS: 0,
      hitsPerPage: 0,
      exhaustiveNbHits: false,
      query: '',
      params: ''
    }))
  });

  /**
   * Custom search client that suppresses unnecessary or partial queries
   */
  const searchClient = {
    ...client,
    search(requests) {
      const isSearchPage = route.value.path.includes('search');

      // Suppress request if all queries are empty or below min chars
      const isEmptyQuery = requests.every(({ params }) => !params?.query || params.query.length < autocompleteMinimumChars);

      // Suppress partial (typically without facets) requests on the search page
      const isPartialRequest =
        isSearchPage &&
        requests.some(({ params }) => {
          const p = params || {};
          return !p.facets?.length;
        });

      if (isEmptyQuery || isPartialRequest) {
        return Promise.resolve(createEmptyResults(requests));
      }

      const simpleKey = normalizeRequest(requests);

      // Return cached promise if query is identical
      if (lastRequestCache.key === simpleKey && lastRequestCache.promise) {
        return lastRequestCache.promise;
      }

      const result = client.search(requests);
      lastRequestCache.key = simpleKey;
      lastRequestCache.promise = result;

      return result;
    }
  } as SearchClient;

  return {
    searchClient: searchClient,
    clientConfig: clientConfig
  };
};
